import { notifyError } from 'config/setupBugsnag';
import RestUtility from '../../../restUtility';
import { dsoOrdersEndpoint } from '../../../restUtility/restEndpoints';
import {
  DsoOrderClient,
  DsoOrderServer,
  DsoShipment,
  DsoShipmentClient,
  DsoTracking,
  DsoTrackingClient,
  DsoLineItem,
  DsoLineItemClient,
} from 'types/dso/DsoOrderDetails.types';

export class DsoOrderDetailsService {
  private restUtility: RestUtility;

  constructor() {
    this.restUtility = new RestUtility();
  }
  /**
   * Fetch detailed order information for a given DSO order ID and
   * transform the server response into the client-side shape used
   * by the application.
   *
   * Purpose:
   * - Retrieve order details from the DSO order history API.
   * - Convert server-side fields into `DsoOrderClient` objects.
   *
   * @param {string} orderId - The ID of the order to fetch.
   * @returns {Promise<DsoOrderClient>} A promise that resolves to a
   *   `DsoOrderClient` object representing the order, or `null` if the
   *   response is missing or has no usable data.
   *
   * Errors:
   * - Throws network or HTTP errors from the underlying `restUtility.get` call
   *   (e.g. timeouts, 4xx/5xx responses). Those errors are reported to
   *   Bugsnag via `notifyError` and then re-thrown to the caller.
   */
  async getOrderDetails(orderId: string): Promise<DsoOrderClient> {
    try {
      const response = await this.restUtility.get<DsoOrderServer>(
        `${dsoOrdersEndpoint}/${orderId}`
      );
      return this.buildOrderDetails(response);
    } catch (error) {
      notifyError('ApiError:: DSO::getOrderDetails', error);
      throw error;
    }
  }

  private buildOrderDetails(response: DsoOrderServer): DsoOrderClient {
    if (!response || !response.data || !response.data.order) {
      return {
        sapOrderCreationStatus: '',
        orderId: '',
        orderNumber: '',
        state: '',
        completedAt: null,
        billAddress: '',
        displayOrderSubTotal: '',
        displayOrderAdjustmentTotal: '',
        displayOrderDiscountTotal: '',
        displayOrderNetTotal: '',
        displayOrderShippingTotal: '',
        displayOrderTaxTotal: '',
        displayOrderGrandTotal: '',
        shipments: [],
        shippingMethod: {
          id: undefined,
          label: '',
        },
        paymentMethod: {
          id: undefined,
          label: '',
        },
        billingAddress: {
          billAccountNumber: '',
          billAddress: '',
        },
        isMultiShipOrder: false,
      };
    }

    const order = response.data.order;
    const sapOrderCreationStatus = response.data.status;

    return {
      sapOrderCreationStatus,
      orderId: order.order_id.toString(),
      orderNumber: order.order_number,
      state: order.state,
      completedAt: order.completed_at ?? null,
      billAddress: order.bill_address,
      displayOrderSubTotal: order.display_order_sub_total,
      displayOrderAdjustmentTotal: order.display_order_adjustment_total,
      displayOrderDiscountTotal: order.display_order_discount_total,
      displayOrderNetTotal: order.display_order_net_total,
      displayOrderShippingTotal: order.display_order_shipping_total,
      displayOrderTaxTotal: order.display_order_tax_total,
      displayOrderGrandTotal: order.display_order_grand_total,
      shipments: this.transformShipments(order.shipments ?? []),
      shippingMethod: {
        id: order.shipping_method?.id,
        label: order.shipping_method?.label,
      },
      paymentMethod: {
        id: order.payment_method?.id,
        label: order.payment_method?.label,
      },
      billingAddress: {
        billAccountNumber: order.billing_address?.bill_account_number,
        billAddress: order.billing_address?.bill_address,
      },
      isMultiShipOrder: order.is_multi_ship_order,
    };
  }

  private parseAddressJson(
    addressJson: string | null
  ): { accountNumber: string; accountName: string; accountAddress: string } | null {
    if (!addressJson) return null;
    try {
      const parsed = JSON.parse(addressJson);
      return {
        accountNumber: parsed.account_number ?? parsed.accountNumber ?? '',
        accountName: parsed.account_name ?? parsed.accountName ?? '',
        accountAddress: parsed.address ?? parsed.accountAddress ?? '',
      };
    } catch {
      return null;
    }
  }

  private transformShipments(shipments: DsoShipment[]): DsoShipmentClient[] {
    return (shipments || []).map(shipment => {
      const shipParsed = this.parseAddressJson(shipment.ship_address);
      const billParsed = this.parseAddressJson(shipment.bill_address);

      return {
        shipAddress: {
          accountNumber: shipParsed?.accountNumber || shipment.ship_account_number || '',
          accountName: shipParsed?.accountName ?? '',
          accountAddress: shipParsed?.accountAddress ?? '',
        },
        billAddress: billParsed
          ? {
              accountNumber: billParsed.accountNumber || shipment.bill_account_number || '',
              accountName: billParsed.accountName,
              accountAddress: billParsed.accountAddress,
            }
          : shipment.bill_account_number
            ? { accountNumber: shipment.bill_account_number, accountName: '', accountAddress: '' }
            : null,
        sapOrderNumber: shipment.sap_order_number,
        displayOrderSubTotal: shipment.display_order_sub_total,
        displayOrderAdjustmentTotal: shipment.display_order_adjustment_total,
        displayOrderNetTotal: shipment.display_order_net_total,
        displayOrderShippingTotal: shipment.display_order_shipping_total,
        displayOrderTaxTotal: shipment.display_order_tax_total,
        displayOrderGrandTotal: shipment.display_order_grand_total,
        displayOrderDiscountTotal: shipment.display_order_discount_total,
        trackings: this.transformTrackings(shipment.trackings || []),
        lineItems: this.transformLineItems(shipment.line_items || []),
      };
    });
  }

  private transformTrackings(trackings: DsoTracking[]): DsoTrackingClient[] {
    return trackings.map(tracking => ({
      trackingNumber: tracking.tracking_number,
      trackingUrl: tracking.tracking_url,
    }));
  }

  private transformLineItems(lineItems: DsoLineItem[]): DsoLineItemClient[] {
    return lineItems.map(item => ({
      name: item.name,
      imageUrl: item.image_url,
      displayUnitPrice: item.display_unit_price,
      displayItemSubTotal: item.display_item_sub_total,
      quantity: item.quantity,
      displayItemDiscountTotal: item.display_item_discount_total,
      discountPercentage: item.discount_percentage,
    }));
  }
}
